import { Injectable } from '@angular/core';
import { map, Observable, shareReplay, switchMap } from 'rxjs';
import { FacilitySlot, SlotGeneratorConfig, WaitlistEntry } from '@models';
import {
  arrayRemove,
  doc,
  getDocs,
  query,
  runTransaction,
  updateDoc,
  writeBatch,
  FieldValue,
} from '@angular/fire/firestore';
import { orderBy, where } from '@angular/fire/firestore';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';
import { format, addDays, parseISO, getDay } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class FacilitySlotService {
  /** All slots ordered by date and startTime asc */
  public all$: Observable<FacilitySlot[]>;

  constructor(
    private readonly firestore: FirestoreService,
    private readonly tenantContext: TenantContextService,
  ) {
    this.all$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilitySlot>(
          this.tenantContext.collections.FACILITY_SLOTS,
          orderBy('date', 'asc'),
          orderBy('startTime', 'asc'),
        ),
      ),
      shareReplay(1),
    );
  }

  /** Slots for a specific facility */
  byFacility$(facilityId: string): Observable<FacilitySlot[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilitySlot>(
          this.tenantContext.collections.FACILITY_SLOTS,
          where('facilityId', '==', facilityId),
          orderBy('date', 'asc'),
          orderBy('startTime', 'asc'),
        ),
      ),
    );
  }

  /** Slots for a specific facility from a date range (inclusive) */
  byFacilityAndDateRange$(
    facilityId: string,
    dateFrom: string,
    dateTo: string,
  ): Observable<FacilitySlot[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilitySlot>(
          this.tenantContext.collections.FACILITY_SLOTS,
          where('facilityId', '==', facilityId),
          where('date', '>=', dateFrom),
          where('date', '<=', dateTo),
          orderBy('date', 'asc'),
          orderBy('startTime', 'asc'),
        ),
      ),
    );
  }

  /** All available (not full, not cancelled) slots for a facility from today onwards */
  available$(facilityId: string): Observable<FacilitySlot[]> {
    const today = format(new Date(), 'yyyy-MM-dd');
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilitySlot>(
          this.tenantContext.collections.FACILITY_SLOTS,
          where('facilityId', '==', facilityId),
          where('date', '>=', today),
          where('status', '==', 'AVAILABLE'),
          orderBy('date', 'asc'),
          orderBy('startTime', 'asc'),
        ),
      ),
    );
  }

  /**
   * All future slots (from today onwards) where the given user is on the waitlist,
   * regardless of the slot status. A user can be added to a waitlist from the admin
   * even if the slot is not marked FULL, so we must NOT filter by status here.
   * Fetches future slots for the tenant and filters client-side since Firestore
   * doesn't support querying by nested array fields.
   */
  fullSlotsWithWaitlist$(userId: string): Observable<FacilitySlot[]> {
    const today = format(new Date(), 'yyyy-MM-dd');
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilitySlot>(
          this.tenantContext.collections.FACILITY_SLOTS,
          where('date', '>=', today),
          orderBy('date', 'asc'),
          orderBy('startTime', 'asc'),
        ),
      ),
      map((slots) =>
        slots.filter((s) => s.waitlist?.some((e) => e.userId === userId)),
      ),
    );
  }
  availableOrFull$(facilityId: string): Observable<FacilitySlot[]> {
    const today = format(new Date(), 'yyyy-MM-dd');
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilitySlot>(
          this.tenantContext.collections.FACILITY_SLOTS,
          where('facilityId', '==', facilityId),
          where('date', '>=', today),
          where('status', 'in', ['AVAILABLE', 'FULL']),
          orderBy('date', 'asc'),
          orderBy('startTime', 'asc'),
        ),
      ),
    );
  }

  /** Slots for a specific facility on a specific date */
  byFacilityAndDate$(
    facilityId: string,
    date: string,
  ): Observable<FacilitySlot[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilitySlot>(
          this.tenantContext.collections.FACILITY_SLOTS,
          where('facilityId', '==', facilityId),
          where('date', '==', date),
          orderBy('startTime', 'asc'),
        ),
      ),
    );
  }

  /** All non-cancelled slots across all facilities in a date range */
  byDateRange$(dateFrom: string, dateTo: string): Observable<FacilitySlot[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilitySlot>(
          this.tenantContext.collections.FACILITY_SLOTS,
          where('date', '>=', dateFrom),
          where('date', '<=', dateTo),
          orderBy('date', 'asc'),
          orderBy('startTime', 'asc'),
        ),
      ),
    );
  }

  /**
   * Bulk-creates FacilitySlot documents from a SlotGeneratorConfig.
   * Iterates every day in [dateFrom, dateTo] and creates a slot for each
   * day that falls on one of the configured daysOfWeek.
   *
   * Uses Firestore writeBatch (max 500 writes per batch).
   * @returns Total number of slots created and the seriesId assigned to all of them
   */
  async generateSlots(
    config: SlotGeneratorConfig,
    facilityName: string,
    bookingMode: FacilitySlot['bookingMode'],
    seriesName?: string,
  ): Promise<{ count: number; seriesId: string }> {
    const {
      facilityId,
      dateFrom,
      dateTo,
      daysOfWeek,
      startTime,
      endTime,
      capacity,
      price,
      waitlistEnabled,
      notes,
    } = config;

    const collectionPath = this.tenantContext.collections.FACILITY_SLOTS;
    const db = this.firestore.db;

    const seriesId = crypto.randomUUID();
    let current = parseISO(dateFrom);
    const end = parseISO(dateTo);

    const slots: Omit<FacilitySlot, 'id'>[] = [];

    while (current <= end) {
      const dayOfWeek = getDay(current); // 0=Sun … 6=Sat
      if (daysOfWeek.includes(dayOfWeek)) {
        slots.push({
          facilityId,
          facilityName,
          bookingMode,
          date: format(current, 'yyyy-MM-dd'),
          startTime,
          endTime,
          capacity: capacity ?? 1,
          bookedCount: 0,
          price: price ?? 0,
          status: 'AVAILABLE',
          waitlistEnabled: waitlistEnabled ?? true,
          notes: notes ?? '',
          seriesId,
          seriesName: seriesName ?? undefined,
        });
      }
      current = addDays(current, 1);
    }

    // Firestore writeBatch is limited to 500 operations — chunk if needed
    const BATCH_LIMIT = 500;
    for (let i = 0; i < slots.length; i += BATCH_LIMIT) {
      const chunk = slots.slice(i, i + BATCH_LIMIT);
      const batch = writeBatch(db);
      for (const slotData of chunk) {
        const colRef = this.firestore.getCollectionRef(collectionPath);
        const docRef = doc(colRef);
        batch.set(docRef, slotData);
      }
      await batch.commit();
    }

    return { count: slots.length, seriesId };
  }

  /**
   * One-time fetch of all slots belonging to a series.
   */
  async getBySeriesId(seriesId: string): Promise<FacilitySlot[]> {
    const collectionPath = this.tenantContext.collections.FACILITY_SLOTS;
    const colRef = this.firestore.getCollectionRef(collectionPath);
    const q = query(colRef, where('seriesId', '==', seriesId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as FacilitySlot);
  }

  /**
   * Bulk-updates all slots in a series with the given fields.
   * Only the provided fields are overwritten; `date`, `facilityId`, etc. are untouched.
   * @returns Number of slots updated
   */
  async updateBySeries(
    seriesId: string,
    data: Partial<
      Pick<
        FacilitySlot,
        | 'startTime'
        | 'endTime'
        | 'capacity'
        | 'price'
        | 'status'
        | 'notes'
        | 'seriesName'
        | 'waitlistEnabled'
      >
    >,
  ): Promise<number> {
    const slots = await this.getBySeriesId(seriesId);
    if (!slots.length) return 0;
    const db = this.firestore.db;
    const collectionPath = this.tenantContext.collections.FACILITY_SLOTS;
    const BATCH_LIMIT = 500;
    for (let i = 0; i < slots.length; i += BATCH_LIMIT) {
      const chunk = slots.slice(i, i + BATCH_LIMIT);
      const batch = writeBatch(db);
      for (const slot of chunk) {
        const colRef = this.firestore.getCollectionRef(collectionPath);
        const docRef = doc(colRef, slot.id);
        batch.update(
          docRef,
          data as Record<string, FieldValue | Partial<unknown> | undefined>,
        );
      }
      await batch.commit();
    }
    return slots.length;
  }

  /**
   * Cancels (sets status = CANCELLED) all slots in a series.
   * @returns Number of slots cancelled
   */
  async cancelBySeries(seriesId: string): Promise<number> {
    return this.updateBySeries(seriesId, { status: 'CANCELLED' });
  }

  /**
   * Deletes all slots in a series.
   * @returns Number of slots deleted
   */
  async deleteBySeries(seriesId: string): Promise<number> {
    const slots = await this.getBySeriesId(seriesId);
    if (!slots.length) return 0;
    const db = this.firestore.db;
    const collectionPath = this.tenantContext.collections.FACILITY_SLOTS;
    const BATCH_LIMIT = 500;
    for (let i = 0; i < slots.length; i += BATCH_LIMIT) {
      const chunk = slots.slice(i, i + BATCH_LIMIT);
      const batch = writeBatch(db);
      for (const slot of chunk) {
        const colRef = this.firestore.getCollectionRef(collectionPath);
        const docRef = doc(colRef, slot.id);
        batch.delete(docRef);
      }
      await batch.commit();
    }
    return slots.length;
  }

  async create(data: Omit<FacilitySlot, 'id'>): Promise<string> {
    return this.firestore.create<FacilitySlot>(
      this.tenantContext.collections.FACILITY_SLOTS,
      data,
    );
  }

  async update(data: Partial<FacilitySlot> & { id: string }): Promise<void> {
    return this.firestore.update<FacilitySlot>(
      this.tenantContext.collections.FACILITY_SLOTS,
      data,
    );
  }

  async cancel(id: string): Promise<void> {
    return this.firestore.update<FacilitySlot>(
      this.tenantContext.collections.FACILITY_SLOTS,
      { id, status: 'CANCELLED' },
    );
  }

  async delete(id: string): Promise<void> {
    return this.firestore.delete(
      this.tenantContext.collections.FACILITY_SLOTS,
      id,
    );
  }

  /**
   * Adds the user to the slot's waitlist. Idempotent *per user*: calling it
   * again for someone already queued is a no-op that keeps their original
   * `joinedAt`, so they do not lose their place in the queue.
   *
   * Deliberately NOT `arrayUnion`: that de-duplicates by deep-equality of the
   * whole object, and every `entry` carries a freshly generated `joinedAt`, so
   * it can never match a stored entry — the same user would be queued twice,
   * counted twice in "N en espera" and promoted twice. The check has to be on
   * `userId` alone, which means reading the array and writing it back.
   *
   * Concurrency: the read-modify-write runs inside a Firestore transaction. If
   * two users join the same slot at once, the one that commits second is
   * detected as stale, aborted and retried by the SDK against the freshly
   * written waitlist, so neither entry is dropped.
   */
  async joinWaitlist(slotId: string, entry: WaitlistEntry): Promise<void> {
    const docRef = this.firestore.getDocumentRef<FacilitySlot>(
      this.tenantContext.collections.FACILITY_SLOTS,
      slotId,
    );
    await runTransaction(this.firestore.db, async (transaction) => {
      const snap = await transaction.get(docRef as any);
      if (!snap.exists()) {
        throw new Error(`Facility slot "${slotId}" does not exist`);
      }
      const waitlist = (snap.data() as FacilitySlot).waitlist ?? [];
      // Already queued → leave the stored entry (and its joinedAt) untouched.
      if (waitlist.some((e) => e.userId === entry.userId)) return;
      transaction.update(docRef as any, { waitlist: [...waitlist, entry] });
    });
  }

  /**
   * Removes the user's entry from the slot's waitlist using Firestore arrayRemove.
   * The entry object must match exactly what was stored.
   */
  async leaveWaitlist(slotId: string, entry: WaitlistEntry): Promise<void> {
    const docRef = this.firestore.getDocumentRef(
      this.tenantContext.collections.FACILITY_SLOTS,
      slotId,
    );
    await updateDoc(docRef as any, { waitlist: arrayRemove(entry) });
  }
}
